import { useEffect, useId, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { HubSupportTicketAttachmentPicker, addHubSupportTicketPastedFiles } from "@/components/HubSupportTicketAttachmentPicker";
import { DocumentModal } from "@/pages/documents/DocumentModal";
import { Button } from "@/components/ui/Button";
import { useHubToast } from "@/components/ui/HubToast";
import { apiFetch, ApiError } from "@/lib/api";
import {
  hubSupportTicketAttachmentApiPayload,
  type HubSupportLocalAttachment,
} from "@/lib/hubSupportTicketAttachments";
import {
  readHubSupportConsoleCapturePayload,
  readHubSupportConsoleCapturePreview,
} from "@/lib/installHubSupportConsoleCapture";
import { hubSupportTicketFieldLabelClassName, hubSupportTicketReporterFieldCopy } from "@/lib/hubSupportTicketFieldCopy";
import {
  hubSupportTicketPagePath,
  hubSupportTicketPageUrlFull,
} from "@/lib/navigationTrail";
import { cn } from "@/lib/utils";

type TicketKind = "bug" | "suggestion";

function HubSupportTicketNewForm({
  onClose,
  onSubmitted,
  initialKind = "bug",
  initialActualBehavior = "",
  initialExpectedBehavior = "",
  pagePathOverride,
  pageUrlFullOverride,
  initialAttachments = [],
  incomingAttachment = null,
  title = "New support ticket",
  description = "Report a bug or leave a suggestion. Recent console output from this browser tab is included when you send.",
}: {
  onClose: () => void;
  onSubmitted: (ticketId: string) => void;
  initialKind?: TicketKind;
  initialActualBehavior?: string;
  initialExpectedBehavior?: string;
  pagePathOverride?: string;
  pageUrlFullOverride?: string;
  initialAttachments?: HubSupportLocalAttachment[];
  incomingAttachment?: HubSupportLocalAttachment | null;
  title?: string;
  description?: string;
}): JSX.Element {
  const formId = useId();
  const [kind, setKind] = useState<TicketKind>(initialKind);
  const [actualBehavior, setActualBehavior] = useState(initialActualBehavior);
  const [expectedBehavior, setExpectedBehavior] = useState(initialExpectedBehavior);
  const [attachments, setAttachments] = useState<HubSupportLocalAttachment[]>(initialAttachments);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fieldCopy = hubSupportTicketReporterFieldCopy(kind);
  const consolePreview = readHubSupportConsoleCapturePreview();

  useEffect(() => {
    if (incomingAttachment == null) return;
    setAttachments((current) =>
      current.some((row) => row.id === incomingAttachment.id) ? current : [...current, incomingAttachment],
    );
  }, [incomingAttachment]);

  async function onSubmit(): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      const result = await apiFetch<{ ok: boolean; id: string }>("/api/hub-support-tickets", {
        method: "POST",
        body: {
          kind,
          actualBehavior,
          expectedBehavior,
          consoleCapture: readHubSupportConsoleCapturePayload(),
          pagePath:
            pagePathOverride ??
            hubSupportTicketPagePath(window.location.pathname, window.location.search),
          pageUrlFull: pageUrlFullOverride ?? hubSupportTicketPageUrlFull(window.location),
          userAgent: navigator.userAgent,
          screenshots: attachments.map((row) => hubSupportTicketAttachmentApiPayload(row)),
        },
      });
      onSubmitted(result.id);
    } catch (caught) {
      const message = caught instanceof ApiError ? caught.message : "Could not send ticket.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <DocumentModal
      title={title}
      showCloseButton={false}
      onClose={onClose}
      description={description}
      shellClassName="max-w-2xl"
      overlayClassName="z-[70] px-4 py-8"
    >
      <form
        id={formId}
        className="space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
        onPaste={async (event) => {
          const added = await addHubSupportTicketPastedFiles(
            event.clipboardData?.items,
            attachments,
            setAttachments,
          );
          if (added) event.preventDefault();
        }}
      >
        <div>
          <span className="mb-2 block text-xs font-medium uppercase tracking-wide text-slate-500">Type</span>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={cn(
                "rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
                kind === "bug"
                  ? "border-brand-500 bg-brand-50 text-brand-800"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
              )}
              onClick={() => setKind("bug")}
            >
              Bug report
            </button>
            <button
              type="button"
              className={cn(
                "rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
                kind === "suggestion"
                  ? "border-brand-500 bg-brand-50 text-brand-800"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
              )}
              onClick={() => setKind("suggestion")}
            >
              Suggestion
            </button>
          </div>
        </div>
        <div>
          <label className={hubSupportTicketFieldLabelClassName(kind)} htmlFor={`${formId}-actual`}>
            {fieldCopy.actualLabel}
          </label>
          <textarea
            id={`${formId}-actual`}
            required
            rows={4}
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            value={actualBehavior}
            onChange={(event) => setActualBehavior(event.target.value)}
            placeholder={fieldCopy.actualPlaceholder}
          />
        </div>
        <div>
          <label className={hubSupportTicketFieldLabelClassName(kind)} htmlFor={`${formId}-expected`}>
            {fieldCopy.expectedLabel}
          </label>
          <textarea
            id={`${formId}-expected`}
            required
            rows={3}
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            value={expectedBehavior}
            onChange={(event) => setExpectedBehavior(event.target.value)}
            placeholder={fieldCopy.expectedPlaceholder}
          />
        </div>
        <HubSupportTicketAttachmentPicker
          attachments={attachments}
          onChange={setAttachments}
          disabled={submitting}
        />
        <div>
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Console capture</span>
          <textarea
            readOnly
            rows={4}
            className="w-full resize-y rounded-md border border-slate-100 bg-slate-50 px-3 py-2 font-mono text-[11px] text-slate-700"
            value={consolePreview}
          />
        </div>
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? "Sending…" : "Send"}
          </Button>
        </div>
      </form>
    </DocumentModal>
  );
}

export type HubSupportTicketModalProps = {
  onClose: () => void;
  initialKind?: TicketKind;
  initialActualBehavior?: string;
  initialExpectedBehavior?: string;
  pagePathOverride?: string;
  pageUrlFullOverride?: string;
  initialAttachments?: HubSupportLocalAttachment[];
  incomingAttachment?: HubSupportLocalAttachment | null;
  title?: string;
  description?: string;
};

export function HubSupportTicketModal(props: HubSupportTicketModalProps): JSX.Element {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();

  return (
    <HubSupportTicketNewForm
      onClose={props.onClose}
      initialKind={props.initialKind}
      initialActualBehavior={props.initialActualBehavior}
      initialExpectedBehavior={props.initialExpectedBehavior}
      pagePathOverride={props.pagePathOverride}
      pageUrlFullOverride={props.pageUrlFullOverride}
      initialAttachments={props.initialAttachments}
      incomingAttachment={props.incomingAttachment}
      title={props.title}
      description={props.description}
      onSubmitted={(ticketId) => {
        void queryClient.invalidateQueries({ queryKey: ["hub-support-tickets", "mine"] });
        showToast({
          message: `Support ticket #${ticketId} was submitted.`,
          tone: "success",
        });
        props.onClose();
      }}
    />
  );
}
